/**
 * iter-338v (BOQEditPage Refactor Phase A) — Vendor Quote Link dialog.
 * iter-364: added
 *   • Per-category scope filter (checkbox list, main-categories only,
 *     shown as "A – Site · B – Electrical" for readability).
 *   • Custom expiry input beyond the preset list (up to 3650 days / 10 yr).
 *   • Post-generation success panel with a dedicated Copy button, matching
 *     the Copy Client / Copy Vendor BOQ link UX on the Worksheet page.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Link as LinkIcon, Copy, Check, Layers, FileText, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendorQuoteDialog({
  open,
  onOpenChange,
  data,
  onDataChange,
  loading,
  onSubmit,
  project,
  worksheet,
  result,       // iter-364: { url, token, expiry_date, vendor_name } after success
  onCopy,       // iter-364: copy the generated URL from the success panel
  onReset,      // iter-364: create another link (returns to the form)
  categories,   // iter-364: [{ group, group_name, ... }] — main categories only
}) {
  // Reduce categories to unique (letter, name) pairs sorted by letter.
  const catRows = (() => {
    const seen = new Map();
    (categories || []).forEach((c) => {
      const letter = (c.group || c.code || '').trim().toUpperCase();
      const name = c.group_name || c.name || '';
      if (letter && !seen.has(letter)) seen.set(letter, name);
    });
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([letter, name]) => ({ letter, name }));
  })();

  const selectedCats = new Set((data.category_codes || []).map((c) => (c || '').toUpperCase()));
  const toggleCat = (letter) => {
    const next = new Set(selectedCats);
    if (next.has(letter)) next.delete(letter); else next.add(letter);
    onDataChange({ ...data, category_codes: [...next] });
  };

  // iter-372 — Preview the project documents that WILL be visible to the
  // vendor when this link is opened. Fetches from the same endpoint the
  // Documents panel uses; server filters on visible_to_vendor=true so
  // admins know exactly what the vendor will see.
  // iter-375 — extended to list ALL project docs (not just vendor-visible
  // ones) with an inline toggle so admins can flip visibility right here
  // without leaving the dialog.
  const [allDocs, setAllDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [togglingId, setTogglingId] = useState('');
  useEffect(() => {
    if (!open || !project?.id) { setAllDocs([]); return; }
    let cancelled = false;
    setDocsLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${project.id}/documents`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (cancelled) return;
        setAllDocs(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setAllDocs([]);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, project?.id]);

  const vendorDocs = allDocs.filter((d) => d.visible_to_vendor);

  const toggleDocVendorVisibility = async (doc) => {
    setTogglingId(doc.id);
    try {
      const nextVal = !doc.visible_to_vendor;
      await axios.put(`${API}/documents/${doc.id}`, {
        name: doc.name,
        url: doc.url,
        doc_type: doc.doc_type || 'other',
        source: doc.source || 'link',
        visible_to_client: !!doc.visible_to_client,
        visible_to_vendor: nextVal,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      // Optimistic local update
      setAllDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, visible_to_vendor: nextVal } : d)));
    } catch (e) {
      console.error(e);
    } finally {
      setTogglingId('');
    }
  };

  const showSuccess = !!result?.url;

  const presetExpiries = [3, 7, 14, 20, 30, 60, 90, 180, 365, 730, 1095, 1825, 3650];
  const isCustomExpiry = !presetExpiries.includes(Number(data.expiry_days));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-vendor-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope text-2xl text-stone-900 flex items-center gap-2">
            <LinkIcon className="text-green-600" />
            {showSuccess ? 'Vendor Quote Link Ready' : 'Create Vendor Quote Link'}
          </DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          // ────────────── SUCCESS PANEL — dedicated copy view ──────────────
          <div className="space-y-4" data-testid="vendor-quote-success">
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm text-emerald-800 flex items-start gap-2">
              <Check size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Link generated for {result.vendor_name}</p>
                {result.expiry_date && (
                  <p className="text-xs text-emerald-700/80 mt-0.5">
                    Expires on {new Date(result.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                )}
                {(data.category_codes || []).length > 0 && (
                  <p className="text-xs text-emerald-700/80 mt-0.5">
                    Scope filtered to categories: {(data.category_codes || []).sort().join(', ')}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-stone-900">Vendor Quote Link</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  readOnly
                  value={result.url}
                  className="bg-stone-50 text-stone-900 font-mono text-xs"
                  data-testid="vendor-quote-generated-url"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  onClick={onCopy}
                  className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
                  data-testid="vendor-quote-copy-btn"
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="text-[10px] text-stone-500 mt-1">
                Share this URL with the vendor via WhatsApp / Email. Anyone with the link can submit rates until the expiry date.
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={onReset} data-testid="vendor-quote-create-another">
                Create another
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="vendor-quote-done">
                Done
              </Button>
            </div>
          </div>
        ) : (
          // ────────────── FORM PANEL ──────────────
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
              <p>Generate a unique public link for a vendor to submit their pricing. The vendor will only see the scope you assign here.</p>
            </div>
            {project?.name && (
              <div className="text-xs text-stone-600">
                <span className="font-semibold">Project:</span> {project.name}
                {worksheet?.worksheet_number && (
                  <> · <span className="font-semibold">BOQ:</span> {worksheet.worksheet_number}</>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-900">Vendor Name *</Label>
                <Input
                  value={data.vendor_name}
                  onChange={(e) => onDataChange({ ...data, vendor_name: e.target.value })}
                  placeholder="e.g., ABC Electricals"
                  className="bg-white text-stone-900"
                  data-testid="vendor-quote-name-input"
                />
              </div>
              <div>
                <Label className="text-stone-900">Vendor Email <span className="text-stone-400 text-xs">(Optional)</span></Label>
                <Input
                  type="email"
                  value={data.vendor_email}
                  onChange={(e) => onDataChange({ ...data, vendor_email: e.target.value })}
                  placeholder="vendor@example.com"
                  className="bg-white text-stone-900"
                  data-testid="vendor-quote-email-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-stone-900">Instructions for Vendor</Label>
              <Textarea
                value={data.instructions}
                onChange={(e) => onDataChange({ ...data, instructions: e.target.value })}
                placeholder="Please provide your best rates..."
                className="bg-white text-stone-900"
                rows={3}
                data-testid="vendor-quote-instructions-input"
              />
            </div>

            {/* iter-364 — Per-category scope filter */}
            {catRows.length > 0 && (
              <div>
                <Label className="text-stone-900 flex items-center gap-1.5">
                  <Layers size={13} className="text-green-700" />
                  Scope by Category <span className="text-stone-400 text-xs">(Optional — leave empty for all categories)</span>
                </Label>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-52 overflow-y-auto p-2 border border-stone-200 rounded-md bg-stone-50">
                  {catRows.map(({ letter, name }) => (
                    <label
                      key={letter}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm text-stone-800"
                      data-testid={`vendor-quote-cat-${letter}`}
                    >
                      <Checkbox
                        checked={selectedCats.has(letter)}
                        onCheckedChange={() => toggleCat(letter)}
                      />
                      <span className="font-mono font-semibold text-stone-500 w-4">{letter}</span>
                      <span className="text-stone-400">—</span>
                      <span className="truncate">{name || 'Unnamed'}</span>
                    </label>
                  ))}
                </div>
                {selectedCats.size > 0 && (
                  <p className="text-[11px] text-emerald-700 mt-1">
                    Vendor will only see: {[...selectedCats].sort().join(', ')} ({selectedCats.size} of {catRows.length})
                  </p>
                )}
              </div>
            )}

            {/* iter-372 + iter-375 — Vendor-visible docs preview WITH inline
                toggle so admin can tick/untick vendor visibility right here
                without opening the Documents panel. */}
            <div className="border border-amber-200 bg-amber-50/30 rounded-md p-3" data-testid="vendor-quote-docs-preview">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-stone-800">
                  <FileText size={14} className="text-amber-700" />
                  Documents Available to Vendor
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                    {docsLoading ? '…' : `${vendorDocs.length} of ${allDocs.length} shared`}
                  </span>
                </div>
                <span className="text-[10px] text-stone-500">
                  Tick to include a document in this vendor&apos;s view
                </span>
              </div>
              {docsLoading ? (
                <div className="text-xs text-stone-400 py-2">Loading project documents…</div>
              ) : allDocs.length === 0 ? (
                <div className="text-xs text-stone-500 py-2 italic">
                  No documents attached to this project. Use the Documents panel under the BOQ to add drawings, site photos, or contracts.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                  {allDocs.map((d) => (
                    <label
                      key={d.id}
                      className={`flex items-center gap-2 text-xs px-2 py-1.5 border rounded cursor-pointer transition-colors ${d.visible_to_vendor ? 'bg-white border-amber-300' : 'bg-stone-50 border-stone-200 hover:border-stone-300'}`}
                      data-testid={`vendor-quote-doc-${d.id}`}
                    >
                      <Checkbox
                        checked={!!d.visible_to_vendor}
                        onCheckedChange={() => toggleDocVendorVisibility(d)}
                        disabled={togglingId === d.id}
                        data-testid={`vendor-quote-doc-toggle-${d.id}`}
                      />
                      <FileText size={12} className={`shrink-0 ${d.visible_to_vendor ? 'text-amber-700' : 'text-stone-400'}`} />
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 truncate text-stone-800 hover:text-blue-700"
                        title={d.name}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {d.name}
                      </a>
                      <span className="text-[9px] uppercase tracking-wide text-stone-400">
                        {(d.doc_type || 'link').replace('_', ' ')}
                      </span>
                      <ExternalLink size={11} className="text-blue-600 shrink-0" />
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* iter-364 — Custom expiry beyond 90 days */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-900">Link Expiry</Label>
                <Select
                  value={isCustomExpiry ? 'custom' : String(data.expiry_days)}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      onDataChange({ ...data, expiry_days: 120 });
                    } else {
                      onDataChange({ ...data, expiry_days: parseInt(value, 10) });
                    }
                  }}
                >
                  <SelectTrigger className="bg-white" data-testid="vendor-quote-expiry-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">6 months</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="730">2 years</SelectItem>
                    <SelectItem value="custom">Custom (days)…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isCustomExpiry && (
                <div>
                  <Label className="text-stone-900">Custom Days</Label>
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    value={data.expiry_days}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(3650, parseInt(e.target.value, 10) || 1));
                      onDataChange({ ...data, expiry_days: n });
                    }}
                    className="bg-white text-stone-900"
                    data-testid="vendor-quote-custom-expiry-input"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="text-stone-900">Cancel</Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="vendor-quote-create-confirm-btn"
              >
                {loading ? 'Creating...' : <><LinkIcon size={16} className="mr-2" />Create Link</>}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
